import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Avatar, Box, Button, Grid, Icon, IconButton, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import backImg from '../assets/images/philipp-katzenberger-iIJrUoeRoCQ-unsplash.jpg';
import logoGreen from '../assets/images/prosecomlogo-green.svg';
import logo from '../assets/images/prosecomlogo-white.svg';
import {
  AuthError,
  AuthStatus,
  resetAuthError,
  selectAuthError,
  selectAuthStatus,
  setAuthStatus,
  setPasswordAndProfile,
  signIn,
} from '../redux/reducers/authenticationReducer';

function Validators({ password }) {
  const { t } = useTranslation();

  function validatorsExpressions(password) {
    return {
      ruleNumber: /^(?=.*[0-9]).{1,}/g.test(password),
      ruleUpper: /^(?=.*[A-Z]).{1,}/g.test(password),
      ruleLower: /^(?=.*[a-z]).{1,}/g.test(password),
      ruleSpecial: /^(?=.*\W).{1,}/g.test(password),
      ruleSpaces: /^(?=.*)(?!.* ).{1,}/g.test(password),
      ruleLength: /^.{8,30}$/g.test(password),
    };
  }
  const validations = validatorsExpressions(password);
  return Object.keys(validations).map((key) => (
    <Stack direction="row" alignItems="center" key={key}>
      <Icon color={validations[key] ? 'success' : 'error'} sx={{ fontSize: '20px', lineHeight: '20px' }}>
        {validations[key]
          ? <CheckCircleIcon sx={{ height: '15px', width: '15px' }} />
          : <CancelIcon sx={{ height: '15px', width: '15px' }} />}
      </Icon>
      <Typography variant="passwordValidators">{t(`login.password.${key}`)}</Typography>
    </Stack>
  ));
}

function Login({
  loginInfo,
  handleChange,
  submitted,
  showPassword,
  setShowPassword,
  handleLogin,
  authError,
  authStatus,
}) {
  const { t } = useTranslation();

  const handleEnterKey = (e) => {
    if (e.keyCode === 13) {
      // keyCode 13 denotes the 'Enter / Return' keypress
      handleLogin();
    }
  };

  return (
    <Grid container>
      <Grid item sx={{ ml: '-30px' }} xs={7}>
        <Paper sx={{ backgroundImage: `url(${backImg})`, backgroundSize: 'cover', height: '100vh', borderRadius: '0px' }}>
          <Stack height="100%" justifyContent="center" alignItems="center">
            <Typography sx={{ mb: '48px' }} variant="loginHeader">{t('login.header')}</Typography>
            <Typography variant="loginText">{t('sidebar.poweredBy')}</Typography>
            <img width="224px" height="40px" src={logo} alt="Prosecom logo" />
          </Stack>
        </Paper>
      </Grid>
      <Grid item sx={{ ml: '30px' }} xs={5}>
        <Stack sx={{ mt: '96px' }} alignItems="center">
          <Avatar variant="login"><LockIcon /></Avatar>
          <Typography id='login-header' variant="signinText">{t('login.signin')}</Typography>
          <Box sx={{
            margin: '32px',
            '& .MuiTextField-root': { my: 2 },
          }}>
            <TextField
              size="small"
              fullWidth
              required
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              subtype="signinField"
              label={t('login.username.title')}
              id="username"
              name="username"
              value={loginInfo.username}
              onChange={(event) => handleChange(event)}
              error={submitted && loginInfo.username === ''}
            />
            <TextField
              size="small"
              fullWidth
              required
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              subtype="signinField"
              label={t('login.password.title')}
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={loginInfo.password}
              onChange={(event) => handleChange(event)}
              error={submitted && loginInfo.password === ''}
              onKeyDown={(e) => handleEnterKey(e)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              id='login-button'
              variant="contained"
              fullWidth
              onClick={() => handleLogin()}
              sx={{ mt: '16px', mx: 0 }}
              disabled={authStatus === AuthStatus.IN_PROGRESS}
            >
              <Typography variant="signinButton">
                {t('login.buttonLogin')}
              </Typography>
            </Button>
            {authError.authErrorType !== AuthError.NONE && <Typography id='login-error' variant="error">{t('error.loginFailed')}</Typography>}
          </Box>
        </Stack>
      </Grid>
    </Grid>
  );
}

function ChangePassword({
  loginInfo,
  profile,
  handleChange,
  handleProfileChange,
  submitted,
  showPassword,
  setShowPassword,
  handleSetPassword,
  authError,
  authStatus,
}) {
  const { t } = useTranslation();

  const handleEnterKey = (e) => {
    if (e.keyCode === 13) {
      // keyCode 13 denotes the 'Enter / Return' keypress
      handleSetPassword();
    }
  };

  return (
    <Box sx={{ maxWidth: '900px', overflow: 'auto', pb: '32px', pr: '4px' }}>
      <Stack spacing={12} sx={{ mt: '96px' }}>
        <Stack alignItems="center" spacing={4}>
          <Typography id='login-header' variant="loginHeader" color="primary.main">{t('login.header')}</Typography>
          <Stack alignItems="center">
            <Typography variant="loginText" color="primary.main">{t('sidebar.poweredBy')}</Typography>
            <img height="40px" width="224px" src={logoGreen} alt="Prosecom logo" />
          </Stack>
        </Stack>
        <Stack spacing={3}>
          <Stack direction="row" sx={{ width: '100%', justifyContent: 'start' }} spacing={5}>
            <Box sx={{ flexGrow: 1 }}>
              <Stack spacing={3}>
                <Typography variant="h6">{t('login.profile.setProfile')}</Typography>
                <TextField
                  size="small"
                  fullWidth
                  required
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  subtype="signinField"
                  label={t('login.profile.firstName')}
                  id="firstName"
                  name="firstName"
                  value={profile.firstName}
                  onChange={(event) => handleProfileChange(event)}
                  error={submitted && profile.firstName === ''}
                />
                <TextField
                  size="small"
                  fullWidth
                  required
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  subtype="signinField"
                  label={t('login.profile.lastName')}
                  id="lastName"
                  name="lastName"
                  value={profile.lastName}
                  onChange={(event) => handleProfileChange(event)}
                  error={submitted && profile.lastName === ''}
                />
                <TextField
                  size="small"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  subtype="signinField"
                  label={t('login.profile.phone')}
                  id="phone"
                  name="phone"
                  value={profile.phone}
                  onChange={(event) => handleProfileChange(event)}
                />
              </Stack>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Stack spacing={3}>
                <Typography id='change-password-prompt' variant="h6">{t('login.changePassword')}</Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  required
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={loginInfo.password}
                  onChange={(event) => handleChange(event)}
                  error={submitted && loginInfo.password === ''}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  subtype="signinField"
                  label={t('login.password.newPassword')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          color="primary"
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  variant="outlined"
                  size="small"
                  required
                  id='repeat-password'
                  name="passwordRepeat"
                  type={showPassword ? 'text' : 'password'}
                  value={loginInfo.passwordRepeat}
                  error={loginInfo.passwordRepeat !== '' && loginInfo.passwordRepeat !== loginInfo.password}
                  onChange={(event) => handleChange(event)}
                  onKeyDown={(e) => handleEnterKey(e)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  subtype="signinField"
                  label={t('login.password.repeatplaceholder')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Stack>
                  {loginInfo.passwordRepeat !== '' && loginInfo.passwordRepeat !== loginInfo.password && (
                    <Typography id='account-creation-error' variant="error">{t('error.passwordMatch')}</Typography>
                  )}
                  <Validators password={loginInfo.password} />
                </Stack>
              </Stack>
            </Box>
          </Stack>
          <Button
            id='change-password-button'
            variant="contained"
            onClick={() => handleSetPassword()}
            disabled={authStatus === AuthStatus.IN_PROGRESS}
            endIcon={<ChevronRightIcon />}
          >
            {t('login.buttonChangePassword')}
          </Button>
        </Stack>
      </Stack>
      {authError.authErrorType !== AuthError.NONE && <Typography variant="error">{t('error.loginFailed')}</Typography>}
    </Box>
  );
}

function LoginView() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authStatus = useSelector(selectAuthStatus);
  const authError = useSelector(selectAuthError);
  const [loginInfo, setLoginInfo] = useState({ username: '', password: '', passwordRepeat: '', previousPassword: '' });
  const [userProfile, setUserProfile] = useState({ firstName: '', lastName: '', phone: '' });
  const [submitted, setSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  function validatePassword() {
    return /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*\W)(?!.* ).{8,30}$/g.test(loginInfo.password);
  }

  function validate() {
    let valid = true;
    switch (authStatus) {
      case AuthStatus.SIGN_IN:
        if (loginInfo.username === '' || loginInfo.password === '') {
          valid = false;
        }
        break;
      case AuthStatus.CHANGE_PW:
        if (loginInfo.passwordRepeat === '' || loginInfo.password !== loginInfo.passwordRepeat || !validatePassword()) {
          valid = false;
        }
        if (userProfile.firstName === '' || userProfile.lastName === '') {
          valid = false;
        }
        break;
      default:
        valid = false;
        break;
    }
    return valid;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    const newState = { ...loginInfo };
    newState[name] = value;
    setLoginInfo(newState);
  };

  const handleProfileChange = (event) => {
    const { name, value } = event.target;
    const newState = { ...userProfile };
    newState[name] = value;
    setUserProfile(newState);
  };

  const handleLogin = () => {
    setSubmitted(false);
    const newInfo = { ...loginInfo, previousPassword: loginInfo.password };
    setLoginInfo(newInfo);
    dispatch(resetAuthError());
    if (validate) {
      dispatch(setAuthStatus(AuthStatus.IN_PROGRESS));
      dispatch(signIn(newInfo));
    } else {
      setSubmitted(true);
    }
  };

  const handleSetPassword = () => {
    setSubmitted(false);
    dispatch(resetAuthError);
    if (validate) {
      dispatch(setAuthStatus(AuthStatus.IN_PROGRESS));
      dispatch(setPasswordAndProfile({ loginInfo, userProfile }));
    } else {
      setSubmitted(true);
    }
  };

  useEffect(() => {
    switch (authStatus) {
      case AuthStatus.CHANGE_PW:
        setLoginInfo({ ...loginInfo, password: '' });
        break;
      case AuthStatus.AUTHENTICATED:
        navigate('/selection');
        break;
      default:
        break;
    }
  }, [authStatus]);

  return (
    <Box display="flex" justifyContent="center" sx={{ height: '100%' }}>
      {authStatus === AuthStatus.SIGN_IN && (
        <Login
          loginInfo={loginInfo}
          handleChange={handleChange}
          submitted={submitted}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          handleLogin={handleLogin}
          authError={authError}
          authStatus={authStatus}
        />
      )}
      {authStatus === AuthStatus.CHANGE_PW && (
        <ChangePassword
          loginInfo={loginInfo}
          profile={userProfile}
          handleChange={handleChange}
          handleProfileChange={handleProfileChange}
          submitted={submitted}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          handleSetPassword={handleSetPassword}
          authError={authError}
          authStatus={authStatus}
        />
      )}
    </Box>
  );
}

export default LoginView;
